import { useSelector } from '@common/performance/useSelector';
import { shallowEqual } from 'react-redux';
import { Store } from 'redux';

import { getCurrentProcessPath } from '../Parser/bindings';
import {
  currentDialogGraphSelector,
  currentGraphSelector,
  dialogProcessIdSelector,
  GetCurrentGraphResult,
  graphPathSelector,
  originalGraphSelector
} from './selectors';
import { BaseState, CommonGraph, Metadata } from './types';

export function useOriginalGraph<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, GetCurrentGraphResult<G>>(originalGraphSelector, shallowEqual);
}

export function useCurrentGraph<G extends CommonGraph>(): GetCurrentGraphResult<G>;
export function useCurrentGraph<G extends CommonGraph, T>(selector: (result: GetCurrentGraphResult<G>) => T): T;
export function useCurrentGraph<G extends CommonGraph, S extends BaseState<G, Metadata>, T>(
  selector?: (result: GetCurrentGraphResult<G>) => T
): GetCurrentGraphResult<G> | T {
  return useSelector<S, GetCurrentGraphResult<G> | T>((state) => {
    const result = currentGraphSelector<G, S>(state);
    return selector ? selector(result) : result;
  }, shallowEqual);
}

export function getCurrentGraphFromStore<G extends CommonGraph, S extends BaseState<G, Metadata>>(store: Store<S>) {
  return currentGraphSelector(store.getState());
}

export function useCurrentGraphPath<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, string>(graphPathSelector);
}

export function useCurrentDialogGraph<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, GetCurrentGraphResult<G>>(currentDialogGraphSelector, shallowEqual);
}

export function useDialogProcessPath<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  const graphPath = useSelector<S, string>(graphPathSelector);
  const componentId = useSelector<S, string | undefined>(dialogProcessIdSelector);
  if (!componentId) {
    return undefined;
  }
  const processPath = getCurrentProcessPath(componentId, graphPath);
  return processPath;
}
